// src/infra/api/WebSocketService.js
function currentTenant() {
  try {
    const qs = new URLSearchParams(window.location.search);
    const fromQuery = (qs.get("tenant") || "").trim();
    if (fromQuery) return fromQuery;
  } catch {}
  const [, maybe] = window.location.pathname.split("/");
  return maybe || null;
}

function buildWsUrl(path = "/iot-hub/ws/tenant", tenant = currentTenant()) {
  if (!tenant) throw new Error("Tenant não identificado");

  let protocol;

  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  ) {
    protocol = "wss";
  } else {
    protocol = window.location.protocol === "https:" ? "wss" : "ws";
  }

  const host = process.env.REACT_APP_WS_HOST || "iot.bioailab.com.br";

  return `${protocol}://${host}${path}?tenant=${encodeURIComponent(tenant)}`;
}

function formatDevice(device) {
  const parsed =
    typeof device.state === "string"
      ? JSON.parse(device.state)
      : device.state || {};
  return {
    serialNumber: device.serialNumber,
    ...parsed,
  };
}

export function formatPayload(raw) {
  const devices = Array.isArray(raw?.devices)
    ? raw.devices.map(formatDevice)
    : [];

  const devicesBySerial = devices.reduce((acc, d) => {
    acc[d.serialNumber] = d;
    return acc;
  }, {});

  return {
    type: raw?.type || "unknown",
    tenant: raw?.tenant || currentTenant(),
    devices,
    devicesBySerial,
    _raw: raw,
  };
}

export function subscribeTenantStream(onFormattedMessage, opts = {}) {
  const {
    path = "/iot-hub/ws/tenant",
    tenant = currentTenant(),
    onOpen,
    onClose,
    onError,
    reconnect = true,
    backoffBaseMs = 600,
    maxBackoffMs = 30000,
  } = opts;

  if (!tenant) throw new Error("Tenant não identificado");

  let ws = null;
  let retries = 0;
  let closedExplicitamente = false;
  let lastSnapshot = { devicesBySerial: {} };

  const connect = () => {
    const url = buildWsUrl(path, tenant);
    ws = new WebSocket(url);

    ws.onopen = () => {
      console.log("%c[WS] Conectado.", "color: #28a745; font-weight: bold;");
      retries = 0;
      onOpen && onOpen();
    };

    ws.onmessage = (evt) => {
      try {
        const raw = JSON.parse(evt.data);

        if (Array.isArray(raw?.devices)) {
          console.log(
            "%c[WS] Recebido Snapshot:",
            "color: #007bff; font-weight: bold;",
            raw.devices.length,
            "dispositivos"
          );
          lastSnapshot = formatPayload(raw);
          onFormattedMessage && onFormattedMessage(lastSnapshot);
          return;
        }

        const isSingleDevice =
          raw?.serialNumber &&
          (raw?.state ||
            raw?.temperatures ||
            raw?.powerSupply ||
            raw?.spectral ||
            raw?.controlState ||
            raw?.epoch != null ||
            raw?.timestamp != null);

        if (isSingleDevice) {
          console.log(
            "%c[WS] Recebido Delta:",
            "color: #17a2b8;",
            raw.serialNumber,
            raw
          );

          let stateObj;
          if (raw.state) {
            stateObj =
              typeof raw.state === "string" ? JSON.parse(raw.state) : raw.state;
          } else {
            const {
              serialNumber,
              type,
              tenant: _tenant,
              devices,
              devicesBySerial,
              _raw,
              ...rest
            } = raw;
            stateObj = rest;
          }

          const formatted = formatDevice({
            serialNumber: raw.serialNumber,
            state: stateObj,
          });

          lastSnapshot = {
            ...lastSnapshot,
            devicesBySerial: {
              ...lastSnapshot.devicesBySerial,
              [formatted.serialNumber]: formatted,
            },
          };

          const formattedEnvelope = {
            type: raw?.type || "delta",
            tenant: raw?.tenant || tenant,
            devices: Object.values(lastSnapshot.devicesBySerial),
            devicesBySerial: { ...lastSnapshot.devicesBySerial },
            _raw: raw,
          };

          onFormattedMessage && onFormattedMessage(formattedEnvelope);
          return;
        }

        console.warn("[WS] Mensagem não reconhecida (ignorada):", raw);
      } catch (e) {
        console.error(
          "%c[WS] Falha ao processar mensagem:",
          "color: red; font-weight: bold;",
          e,
          evt.data
        );
        onError && onError(e);
      }
    };

    ws.onerror = (e) => {
      console.error(
        "%c[WS] Erro na conexão:",
        "color: red; font-weight: bold;",
        e
      );
      onError && onError(e);
    };

    ws.onclose = () => {
      console.log("%c[WS] Desconectado.", "color: #dc3545; font-weight: bold;");
      onClose && onClose();

      if (!closedExplicitamente && reconnect) {
        // Backoff exponencial com teto (não desiste depois de N tentativas —
        // uma queda mais longa do servidor não deve exigir F5 do usuário pra
        // recuperar a conexão, só demora um pouco mais entre tentativas).
        const wait = Math.min(backoffBaseMs * Math.pow(2, retries), maxBackoffMs);
        retries += 1;
        console.log(`[WS] Tentando reconectar em ${wait}ms... (tentativa ${retries})`);
        setTimeout(connect, wait);
      }
    };
  };

  connect();

  return {
    close() {
      closedExplicitamente = true;
      try {
        ws && ws.close();
      } catch {}
    },
    getState() {
      return lastSnapshot;
    },
  };
}

export function getDevicesSnapshot({
  timeoutMs = 10000,
  path = "/iot-hub/ws/tenant",
} = {}) {
  return new Promise((resolve, reject) => {
    let resolved = false;
    let timer;

    const ctrl = subscribeTenantStream(
      (formatted) => {
        if (
          !resolved &&
          formatted.type === "snapshot" &&
          formatted.devices?.length >= 0
        ) {
          resolved = true;
          clearTimeout(timer);
          ctrl.close();
          resolve(formatted);
        }
      },
      {
        path,
        reconnect: false,
        onError: (e) => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timer);
            ctrl.close();
            reject(e);
          }
        },
      }
    );

    timer = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        ctrl.close();
        reject(new Error("Timeout aguardando snapshot do WebSocket"));
      }
    }, timeoutMs);
  });
}
